import type { FC } from "react";

import { providerRowLabel } from "../../constants/embeddingReadiness";
import {
  MULTI_FIELD_PROVIDER_KEYS,
  PROVIDERS,
  PROVIDER_LABELS,
} from "../../constants/providers";
import { useApiKeys } from "../../hooks/useApiKeys";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { isAdmin } from "../../utils/rbac";
import { Alert } from "../common/Alert";
import { EmbeddingReadinessNotice } from "../common/EmbeddingReadinessNotice";
import { EmptyState } from "../common/EmptyState";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { ProviderKeyRow } from "../common/ProviderKeyRow";

export const ApiKeysPanel: FC = () => {
  const auth = useDashboardAuth();
  const canView = isAdmin(auth.orgRole, auth.globalRole);
  const { keys, loading, error } = useApiKeys(auth.organizationId);

  if (!auth.organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Select an organization to manage API keys."
      />
    );
  }

  if (!canView) {
    return (
      <Alert variant="info">
        API key status requires admin access. Contact your organization
        administrator for details.
      </Alert>
    );
  }

  if (loading) {
    return <LoadingState message="Loading API keys..." />;
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  return (
    <PanelSection title="Provider API Keys">
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        API keys are managed by your platform administrator.
      </p>
      <EmbeddingReadinessNotice className="mb-4" keys={keys} />
      {PROVIDERS.map((provider) => {
        const keyFields = MULTI_FIELD_PROVIDER_KEYS[provider];
        if (keyFields) {
          return (
            <div key={provider}>
              {keyFields.map((field) => (
                <ProviderKeyRow
                  key={`${provider}-${field.value}`}
                  provider={provider}
                  label={`${PROVIDER_LABELS[provider] ?? provider} — ${field.label}`}
                  config={findProviderKeyConfig(keys, provider, field.value)}
                />
              ))}
            </div>
          );
        }
        return (
          <ProviderKeyRow
            key={provider}
            provider={provider}
            label={providerRowLabel(provider)}
            config={findProviderKeyConfig(keys, provider)}
          />
        );
      })}
    </PanelSection>
  );
};

function findProviderKeyConfig(
  keys: ReturnType<typeof useApiKeys>["keys"],
  provider: string,
  providerKeyId?: string,
) {
  return keys.find(
    (key) =>
      key.provider === provider &&
      (providerKeyId === undefined ||
        providerKeyIdMatches(key.provider_key_id, providerKeyId)),
  );
}

function providerKeyIdMatches(
  actualProviderKeyId: string | null,
  expectedProviderKeyId: string,
): boolean {
  const actual = actualProviderKeyId ?? "default";
  if (expectedProviderKeyId === "apiKey") {
    return actual === "apiKey" || actual === "default";
  }
  return actual === expectedProviderKeyId;
}
